import {
  ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';

import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogConfig } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_ICON_DEFAULT_OPTIONS } from '@angular/material/icon';
import {
  provideRouter,
  withComponentInputBinding,
  withViewTransitions,
  type ActivatedRouteSnapshot,
} from '@angular/router';

import { provideState, provideStore, Store } from '@ngrx/store';
import { provideEffects } from '@ngrx/effects';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { filter, firstValueFrom, take } from 'rxjs';

import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { getStorage, provideStorage } from '@angular/fire/storage';

import { routes } from './app.routes';
import { environment } from '../environments/environment';

import { authFeature } from './auth-core/store/auth.feature';
import { AuthEffects } from './auth-core/store/auth.effects';
import { AuthActions } from './auth-core/store/auth.actions';
import { selectRestored } from './auth-core/store/auth.selectors';
import { authInterceptor } from './auth-core/interceptors/auth.interceptor';

/**
 * Read the top-level `animation` tag (`auth` vs `main`) for a route snapshot by
 * walking from the root to the first node that carries one. Only the two
 * top-level routes are tagged, so this identifies which top-level state a
 * navigation lands in.
 */
function topLevelAnimation(snapshot: ActivatedRouteSnapshot | null): string | undefined {
  for (let node = snapshot; node; node = node.firstChild) {
    const animation = node.data?.['animation'] as string | undefined;
    if (animation) {
      return animation;
    }
  }
  return undefined;
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),

    provideRouter(
      routes,
      withComponentInputBinding(),
      withViewTransitions({
        skipInitialTransition: true,
        onViewTransitionCreated: ({ transition, from, to }) => {
          const fromAnim = topLevelAnimation(from);
          const toAnim = topLevelAnimation(to);

          // The directional slide is reserved for the auth <-> main boundary.
          // Navigating between feature screens stays within the shell (same
          // top-level state), so skip the document-level transition entirely —
          // the drawer content fades locally instead (see MainShell).
          if (fromAnim === toAnim) {
            transition.skipTransition();
            return;
          }

          // main -> auth (logout) slides back; auth -> main slides forward.
          document.documentElement.classList.toggle('back', toAnim === 'auth');
        },
      }),
    ),

    provideHttpClient(withInterceptors([authInterceptor])),

    // Icons are Material Symbols Outlined (variable font) per the design
    // system; active states flip the FILL axis in CSS.
    {
      provide: MAT_ICON_DEFAULT_OPTIONS,
      useValue: { fontSet: 'material-symbols-outlined' },
    },

    // Compact-console rule (default-m3-theme skill): no permanently reserved
    // subscript band under form fields — the hint/error area takes space only
    // where one exists.
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic' },
    },

    // ... and EVERY dialog is a data-entry surface here, so the scoped
    // compact treatment (styles.scss .app-compact-dialog: density -4 fields,
    // 14px field text) is the app-wide dialog default. NOTE: a dialog passing
    // its own panelClass replaces this default — include 'app-compact-dialog'
    // in that class list unless the dialog is deliberately roomy. Spread of
    // MatDialogConfig keeps the class's other defaults (hasBackdrop, ...)
    // intact.
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { ...new MatDialogConfig(), panelClass: 'app-compact-dialog' },
    },

    provideStore({}),
    provideState(authFeature),
    provideEffects(AuthEffects),
    // Register app-wide feature slices here only for shared reference data;
    // feature-local slices belong on their route (see app.routes.ts).
    provideStoreDevtools({
      maxAge: 25,
      logOnly: false,
    }),

    // Rehydrate the session before the first route activates so refreshing on a
    // main route does not flash through /auth.
    provideAppInitializer(() => {
      const store = inject(Store);
      store.dispatch(AuthActions.restoreSession());
      return firstValueFrom(
        store.select(selectRestored).pipe(
          filter((restored) => restored),
          take(1),
        ),
      );
    }),

    provideFirebaseApp(() => initializeApp(environment.firebase)),

    provideAuth(() => {
      const auth = getAuth();

      auth.tenantId = environment.firebaseTenantId;

      return auth;
    }),

    provideFirestore(() => getFirestore()),
    provideStorage(() => getStorage()),
  ],
};
